import { THEME_STORAGE_KEY } from '../../modules/theme/consts';
import { prefixKey, STORAGE_PREFIX } from '../../utils/localStorage';

/** Channel every tab listens on to learn the session identity changed (Login As / Switch back). */
const IDENTITY_CHANGE_CHANNEL_NAME = 'bl-identity-change';

const IDENTITY_CHANGED_MESSAGE = 'identity-changed';

/** Storage keys that survive an identity change — device-level preferences, not user data. */
const KEPT_STORAGE_KEYS = new Set([prefixKey(THEME_STORAGE_KEY)]);

/**
 * Removes all persisted app state (zustand `persist` stores, flags) except
 * device-level preferences, so drafts and progress of the previous identity
 * can't leak into the next one.
 */
const clearUserScopedStorage = () => {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(STORAGE_PREFIX) && !KEPT_STORAGE_KEYS.has(key))
      .forEach((key) => localStorage.removeItem(key));
  } catch {
    // Storage inaccessible — the reload below still drops all in-memory state.
  }
};

/**
 * Continues the tab as the new identity: clears user-scoped persisted storage and
 * hard-reloads. The reload drops the in-memory access token, the TanStack Query
 * cache and in-memory stores; the fresh boot exchanges the cookie for a token of
 * the new identity and refetches auth/me.
 */
const reloadAsNewIdentity = () => {
  clearUserScopedStorage();
  window.location.reload();
};

const createChannel = () =>
  typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(IDENTITY_CHANGE_CHANNEL_NAME);

const noop = () => undefined;

/**
 * Called by the tab that detected an identity change on token refresh: tells the
 * other open tabs to switch, then reloads this tab as the new identity.
 */
export const notifyIdentityChanged = () => {
  createChannel()?.postMessage(IDENTITY_CHANGED_MESSAGE);
  reloadAsNewIdentity();
};

/**
 * Subscribes this tab to identity-change notifications from other tabs; on a
 * notification the tab reloads as the new identity. Returns an unsubscribe.
 */
export const subscribeToIdentityChange = (): (() => void) => {
  const channel = createChannel();

  if (channel === null) return noop;

  channel.onmessage = () => reloadAsNewIdentity();

  return () => channel.close();
};
